import type { YMapGroupEntity, YMapComplexEntity } from '@yandex/ymaps3-types'
import type { Ref, VNode, Component } from 'vue'
import type { DispatcherWorkplaceDataEntity } from '@/modules/DispatcherWorkplaceModule/types'

import { h, createApp } from 'vue'

import CheckboxControlComponent from '@/modules/YandexMap3Module/components/YandexMap3Checkbox/index.vue'
import IconButtonControlComponent from '@/modules/YandexMap3Module/components/YandexMap3IconButton/index.vue'
import SearchControlComponent from '@/modules/YandexMap3Module/components/YandexMap3Search/index.vue'

type CheckboxControlProps = {
    checked: Ref<boolean>
    label?: string | null
    onChange: (event: Event) => void
}

type IconButtonControlProps = {
    checked: Ref<boolean>
    icon: VNode | Component
    onChange: (event: Event) => void
}

type SearchControlProps = {
    value: Ref<string | null>
    options: Ref<any>
    placeholder: string | null
    option: VNode | Component
    onInput: (event: Event) => void
    onOptionClicked: (entity: any) => void
}

export function createHorizontalControlsGroup(): YMapGroupEntity<{}> {
    class HorizontalControlsGroup extends ymaps3.YMapGroupEntity<{}> {
        private _element: HTMLDivElement | null
        private _detachDom: (() => void) | null
    
        _createElement() {
            const element = document.createElement('div')
            element.className = 'yandex-map-3-controls controls--horizontal'
            return element
        }
    
        _onAttach() {
            this._element = this._createElement()
            this._detachDom = ymaps3.useDomContext(this, this._element, this._element)
        }
    
        _onDetach() {
            if (this._detachDom) {
                this._detachDom()
            }
    
            this._detachDom = null
            this._element = null
        }
    }

    return new HorizontalControlsGroup({})
}

export function createCheckboxControl(props: CheckboxControlProps): YMapComplexEntity<CheckboxControlProps> {
    class CheckboxControl extends ymaps3.YMapComplexEntity<CheckboxControlProps> {
        private _element: HTMLDivElement | null
        private _detachDom: (() => void) | null
    
        _createElement(props: CheckboxControlProps) {
            const { checked, label, onChange } = props
    
            const element = document.createElement('div')
            element.classList.add('yandex-map-3-control')
            element.classList.add('control--overflow')
    
            const app = createApp({
                render: () => h(CheckboxControlComponent, {
                    checked,
                    label,
                    onChange
                })
            })
        
            app.mount(element)
    
            return element
        }
    
        _onAttach() {
            this._element = this._createElement(this._props)
            this._detachDom = ymaps3.useDomContext(this, this._element, this._element)
        }
    
        _onDetach() {
            if (this._detachDom) {
                this._detachDom()
            }
            
            this._detachDom = null
            this._element = null
        }
    }

    return new CheckboxControl(props)
}

export function createIconButtonControl(props: IconButtonControlProps): YMapComplexEntity<IconButtonControlProps> {
    class IconButtonControl extends ymaps3.YMapComplexEntity<IconButtonControlProps> {
        private _element: HTMLDivElement | null
        private _detachDom: (() => void) | null
    
        _createElement(props: IconButtonControlProps) {
            const { checked, onChange } = props
    
            const element = document.createElement('div')
            element.classList.add('yandex-map-3-control')
            element.classList.add('control--overflow')
    
            const app = createApp({
                render: () => h(IconButtonControlComponent, {
                    checked,
                    onChange
                }, {
                    default: () => h(props.icon)
                })
            })
        
            app.mount(element)
    
            return element
        }
    
        _onAttach() {
            this._element = this._createElement(this._props)
            this._detachDom = ymaps3.useDomContext(this, this._element, this._element)
        }
    
        _onDetach() {
            if (this._detachDom) {
                this._detachDom()
            }
            
            this._detachDom = null
            this._element = null
        }
    }

    return new IconButtonControl(props)
}

export function createSearchControl(props: SearchControlProps): YMapComplexEntity<SearchControlProps> {
    class SearchControl extends ymaps3.YMapComplexEntity<SearchControlProps> {
        private _element: HTMLDivElement | null
        private _detachDom: (() => void) | null
    
        _createElement(props: SearchControlProps) {
            const { value, placeholder, options, onInput, onOptionClicked } = props
    
            const element = document.createElement('div')
            element.classList.add('yandex-map-3-control')
    
            const app = createApp({
                render: () => h(SearchControlComponent as Component<{}>, {
                    value,
                    placeholder,
                    options,
                    onOptionClicked,
                    onInput
                }, {
                    option: (slotProps: { option: DispatcherWorkplaceDataEntity }) => h(props.option, {
                        entity: slotProps.option
                    })
                })
            })
        
            app.mount(element)
    
            return element
        }
    
        _onAttach() {
            this._element = this._createElement(this._props)
            this._detachDom = ymaps3.useDomContext(this, this._element, this._element)
        }
    
        _onDetach() {
            if (this._detachDom) {
                this._detachDom()
            }
            
            this._detachDom = null
            this._element = null
        }
    }

    return new SearchControl(props)
}

export default {
    createHorizontalControlsGroup,
    createCheckboxControl,
    createIconButtonControl,
    createSearchControl,
}